import { crossBrowserExitFullscreen, getCrossBrowserFullscreenElement } from 'util/fullscreen';
import { ReactElement, useContext, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useMediaQuery } from 'react-responsive';
import { useTranslation } from 'react-i18next';
import { DisplayContext } from 'contexts';
import { AnimatedReactions } from 'features/Reactions';
import {
    selectFirstScreenSharingTrack,
    selectIsScreenShared,
    selectParticipants,
    selectReactions,
} from 'state/selectors';
import { LoadingDots } from '@web-chat-core/ui-kit';
import { WaitingRequestsBanner } from 'features/AccessRequests/components/WaitingRequestsBanner';
import { AudioRenderer } from '../../core';
import { STAGE_LAYOUT, MOBILE_STAGE_VIEW_WIDTH } from '../../consts';
import { GridStage } from './GridStage/GridStage';
import { FocusStage } from './FocusStage/FocusStage';
import { MobileStage } from './MobileStage/MobileStage';
import { StageProps } from './types';
import { LoadingAnimation, ParticipantsContainer, StageSurface } from './view';

const StageContent = observer(({ stageProps, mainElement }: { stageProps: StageProps; mainElement: ReactElement }) => {
    const reactions = selectReactions();

    return (
        <StageSurface>
            <WaitingRequestsBanner />
            <ParticipantsContainer>{mainElement}</ParticipantsContainer>

            <AnimatedReactions
                reactions={reactions}
                localParticipantId={stageProps.roomState.room?.localParticipant.identity}
            />

            {stageProps.roomState.audioTracks.map((track) => (
                <AudioRenderer key={track.sid} track={track} isLocal={false} />
            ))}
        </StageSurface>
    );
});

StageContent.displayName = 'StageContent';

export const StageView = observer((stageProps: StageProps) => {
    const { t } = useTranslation();
    const { error, isConnecting, room } = stageProps.roomState;
    const isMobile = useMediaQuery({ query: `(max-width: ${MOBILE_STAGE_VIEW_WIDTH}px)` });
    const context = useContext(DisplayContext);
    const participants = selectParticipants();

    const isScreenShared = selectIsScreenShared();
    const screenTrack = selectFirstScreenSharingTrack();
    const isScreenShareMode = Boolean(isScreenShared && screenTrack);

    useEffect(() => {
        // Выходим из fullscreen если отключена демонстрация экрана
        if (!isScreenShareMode) {
            const fullscreenElement = getCrossBrowserFullscreenElement();

            if (fullscreenElement) {
                crossBrowserExitFullscreen();
            }
        }
    }, [isScreenShareMode]);

    let mainElement: ReactElement;
    if (isMobile) {
        mainElement = <MobileStage {...stageProps} />;
    } else {
        if (context.stageLayout === STAGE_LAYOUT.GRID && !isScreenShared) {
            mainElement = <GridStage {...stageProps} />;
        } else {
            mainElement = <FocusStage {...stageProps} />;
        }
    }

    if (error) {
        return <div>error {error.message}</div>;
    }

    if (isConnecting) {
        return (
            <LoadingAnimation>
                <LoadingDots />
            </LoadingAnimation>
        );
    }
    if (!room) {
        return <div>{t('web_call_room_closed')}</div>;
    }

    if (participants.length === 0) {
        return <div>{t('web_call_participants_room')}</div>;
    }

    return <StageContent stageProps={stageProps} mainElement={mainElement} />;
});

StageView.displayName = 'StageView';
